import { useEffect } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { AuthCard, FormError, FormNotice } from "@/features/auth/AuthCard";
import { Spinner } from "@/ui/Button";
export const Route = createFileRoute("/_public/close-account")({
  component: CloseAccountScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
});

/**
 * The other end of the confirmation email.
 *
 * Public, because the link is the authorisation: requiring a live session here
 * would mean a link that stops working the moment somebody signs out, which is
 * exactly what a person who has decided to leave does.
 *
 * It fires on load rather than asking again. The question was asked in the
 * product, the answer was confirmed by opening the mailbox, and a third
 * "are you sure" is a dialog people learn to click through.
 */
function CloseAccountScreen() {
  const { token } = useSearch({ from: "/_public/close-account" });

  const close = useMutation({
    mutationFn: () => api.post<void>("/auth/close-account", { token }),
  });

  useEffect(() => {
    if (token) close.mutate();
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <AuthCard title="Nothing to confirm" subtitle="This link is missing its token.">
        <FormNotice title="Ask again from your account settings.">
          A confirmation link works once and expires after an hour.
        </FormNotice>
      </AuthCard>
    );
  }

  if (close.isSuccess) {
    return (
      <AuthCard
        title="Your account is closed"
        subtitle="It and everything only it owned have been deleted."
      >
        <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
          Nothing of yours remains on the platform. Thank you for trying Drumlin. If you come
          back, you are welcome to register again with the same address.
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Closing your account" subtitle="This takes a moment.">
      {close.isError ? (
        <FormError error={close.error}>
          <Link to="/sign-in" className="mt-1 inline-block font-medium underline">
            Back to sign in
          </Link>
        </FormError>
      ) : (
        <div className="flex items-center gap-3 text-[13px] text-[var(--text-secondary)]">
          <span className="relative h-4 w-4 text-[var(--primary)]">
            <Spinner />
          </span>
          Deleting your account and everything only it owned.
        </div>
      )}
    </AuthCard>
  );
}
